import type { ServiceAuditSource } from "shared";

/**
 * Every source the audit service is known to send.
 *
 * Kept as a runtime array, not just the `ServiceAuditSource` type, because the
 * check has to happen at runtime — the wire is what lies, and a type cannot
 * narrow what a type was wrong about.
 */
const AUDIT_SOURCES: readonly ServiceAuditSource[] = [
  "manual",
  "automation",
  "api",
  "sla",
  "system",
];

/**
 * Narrows the raw `source` string off the wire to a source we can actually
 * render, degrading to `"manual"` for anything else.
 *
 * `"manual"` is the right degradation because it is the branch that renders
 * `activity_by` — the human name and avatar. Everything unrecognised therefore
 * falls back to the person who is already on the entry, rather than to a label
 * we do not have.
 *
 * This exists because the service sends values outside its own enum: live UAT
 * on 2026-09-04 carried `source: "b3f8e1a2-…"`, an automation-rule uuid in the
 * field that is meant to say what KIND of actor performed the action. Before
 * this, `SOURCE_LABELS[uuid]` returned `undefined` and the row read
 * "undefined updated status from New to Assigned" — with the actor's real name
 * sitting unused on the same entry. A missing/blank `source` is the same case:
 * entries predating 2026-08-17 carry none, and the service's guidance is to
 * read those as manual.
 *
 * Pure and dependency-free on purpose — the drawer logs the rejected value, so
 * this file stays testable in Vitest's node environment.
 */
export const parseAuditSource = (
  raw: string | null | undefined,
): ServiceAuditSource =>
  AUDIT_SOURCES.includes(raw as ServiceAuditSource)
    ? (raw as ServiceAuditSource)
    : "manual";

/** True when `raw` is a value the timeline has no copy or glyph for. */
export const isUnknownAuditSource = (raw: string | null | undefined): boolean =>
  typeof raw === "string" &&
  raw.trim() !== "" &&
  !AUDIT_SOURCES.includes(raw as ServiceAuditSource);
